import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Popover from "@mui/material/Popover";
import cn from "classnames";
import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import PlusIcon from "assets/icons/PlusIcon.svg?react";
import TabsMenuIcon from "assets/icons/TabsMenuIcon.svg?react";

import Tab, { TabStatus } from "pages/DevelopWorkspace/Editor/TabsBar/Tab/Tab";
import TabGroupPanel from "pages/DevelopWorkspace/Editor/TabsBar/TabGroupPanel/TabGroupPanel";
import TabGroupTab from "pages/DevelopWorkspace/Editor/TabsBar/TabGroupTab/TabGroupTab";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useDocumentsActionConfirmation } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocumentsActionConfirmation";
import {
  Script,
  useScripts,
} from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { useShortcutsOverlay } from "pages/DevelopWorkspace/contexts/ShortcutsOverlayContext/ShortcutsOverlayContext";
import { documentHasErrors } from "pages/DevelopWorkspace/helpers/documentHasError";
import { documentHasResults } from "pages/DevelopWorkspace/helpers/documentHasResults";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";
import { isDocumentRunning } from "pages/DevelopWorkspace/helpers/isDocumentRunning";
import { SHORTCUTS } from "pages/DevelopWorkspace/services/shortcuts/constants";
import { getShortcutHint } from "pages/DevelopWorkspace/services/shortcuts/helpers/getShortcutHint";
import { ShortcutAction } from "pages/DevelopWorkspace/services/shortcuts/types";
import useShortcutsHandler from "pages/DevelopWorkspace/services/shortcuts/useShortcutsHandler";
import {
  QueryStatementResult,
  SortOrder,
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import ContextMenu from "components/ContextMenu/ContextMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuHeader from "components/ContextMenu/ContextMenuHeader";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import ContextMenuItemsGroup from "components/ContextMenu/ContextMenuItemsGroup";
import { useLayoutToggles } from "components/LayoutToggles/context";
import Tooltip from "components/Tooltip/Tooltip";

import styles from "./TabsBar.module.scss";

export const getDocumentStatus = (
  document: WorkspaceDocument,
  qsResults: {
    [qsId: string]: QueryStatementResult | undefined;
  }
) => {
  if (isDocumentRunning(document)) {
    return TabStatus.Running;
  }

  if (documentHasErrors(document)) {
    return TabStatus.Error;
  }

  if (documentHasResults(document, qsResults)) {
    return TabStatus.Success;
  }

  return undefined;
};

/*
A group constitutes its chip plus the run of tabs that follow it,
which are all sortable siblings.
The chip needs an Id of its own to be dragged, and this must not
collide with a document Id.
 */
const GROUP_SORTABLE_PREFIX = "tab-group:";

const toGroupSortableId = (tabGroupId: string) =>
  `${GROUP_SORTABLE_PREFIX}${tabGroupId}`;

const toTabGroupId = (sortableId: string) =>
  sortableId.startsWith(GROUP_SORTABLE_PREFIX)
    ? sortableId.slice(GROUP_SORTABLE_PREFIX.length)
    : null;

interface TabWrapperProps {
  document: WorkspaceDocument;
  handleDeleteDocument: (documentId: string) => void;
  handleDeleteAllDocuments: (documentIds: string[]) => void;
  handleSetDocumentTabGroup: (
    documentId: string,
    groupId: string | null
  ) => void;
  handleCreateTabInGroup: (groupId: string) => void;
  handleCreateTabGroup: (
    tabGroup: Omit<TabGroup, "id" | "collapsed">,
    documentIds: string[]
  ) => void;
  hasScroll: boolean;
  lastScrollableTab: boolean;
  tabWidth: number;
  tabRefs: React.MutableRefObject<Record<string, HTMLDivElement>>;
  isDragged: boolean;
  script: Script | null;
  isUnsaved: boolean;
  tabGroup: TabGroup | null;
  withGap: boolean;
  lastInGroup: boolean;
}

const TabWrapper = (props: TabWrapperProps) => {
  const {
    document,
    tabWidth,
    handleDeleteDocument,
    handleDeleteAllDocuments,
    handleSetDocumentTabGroup,
    handleCreateTabInGroup,
    handleCreateTabGroup,
    lastScrollableTab,
    tabRefs,
    isDragged,
    script,
    isUnsaved,
    tabGroup,
    withGap,
    lastInGroup,
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: document.id,
    });

  const {
    state: { activeDocumentId },
    qsResults,
    actions: { setActiveDocument, renameDocument },
  } = useDocuments();

  const status = getDocumentStatus(document, qsResults);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    width: tabWidth + "px",
    ...(tabGroup && { "--tab-group-color": tabGroup.color }),
  } as React.CSSProperties;

  return (
    <div
      key={document.id}
      className={cn(styles.tab, {
        [styles.lastScrollableTab]: lastScrollableTab,
        [styles.grouped]: !!tabGroup && !isDragged,
        [styles.groupEnd]: lastInGroup && !isDragged,
        [styles.withGap]: withGap,
      })}
      data-testid={`document-tab-${script?.title}`}
      style={style}
      ref={ref => {
        if (!ref) {
          return;
        }

        setNodeRef(ref);

        tabRefs.current[document.id] = ref;
      }}
      {...attributes}
      {...listeners}
    >
      {isDragged ? (
        <div className={styles.tabDragPlaceholder} />
      ) : (
        <Tab
          documentId={document.id}
          groupId={document.groupId}
          onSetTabGroup={handleSetDocumentTabGroup}
          onCreateTabInGroup={handleCreateTabInGroup}
          onCreateTabGroup={handleCreateTabGroup}
          isLoadingScript={!script}
          title={script?.title}
          content={script?.editorState.doc}
          isUnsaved={isUnsaved}
          isActive={document.id === activeDocumentId}
          onSelect={() => setActiveDocument(document.id)}
          onClose={(closeAllOther: boolean) => {
            if (closeAllOther) {
              handleDeleteAllDocuments([document.id]);
              return;
            }

            handleDeleteDocument(document.id);
          }}
          onRenameSubmit={(newTitle: string) => {
            renameDocument(document.id, newTitle);
          }}
          status={status}
        />
      )}
    </div>
  );
};

interface TabGroupWrapperProps {
  tabGroup: TabGroup;
  memberTitles: string[];
  collapsed: boolean;
  isDragged: boolean;
  lastInGroup: boolean;
  lastScrollableTab: boolean;
  tabWidth: number;
  onToggleCollapsed: () => void;
  onUngroupAll: () => void;
  onDeleteGroup: () => void;
  onCreateTab: () => void;
}

const TabGroupWrapper = (props: TabGroupWrapperProps) => {
  const {
    tabGroup,
    memberTitles,
    collapsed,
    isDragged,
    lastInGroup,
    lastScrollableTab,
    tabWidth,
    onToggleCollapsed,
    onUngroupAll,
    onDeleteGroup,
    onCreateTab,
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: toGroupSortableId(tabGroup.id),
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    width: tabWidth + "px",
    "--tab-group-color": tabGroup.color,
  } as React.CSSProperties;

  return (
    <div
      className={cn(styles.tab, {
        [styles.grouped]: !isDragged,
        [styles.groupEnd]: lastInGroup && !isDragged,
        [styles.lastScrollableTab]: lastScrollableTab,
      })}
      style={style}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-expanded={!collapsed}
    >
      {isDragged ? (
        <div className={styles.tabDragPlaceholder} />
      ) : (
        <TabGroupTab
          tabGroup={tabGroup}
          memberTitles={memberTitles}
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          onUngroupAll={onUngroupAll}
          onDeleteGroup={onDeleteGroup}
          onCreateTab={onCreateTab}
        />
      )}
    </div>
  );
};

const DraggedTab = forwardRef<
  HTMLDivElement,
  { title: string } & React.HTMLAttributes<HTMLDivElement>
>(({ title, ...props }, ref) => {
  return (
    <div
      {...props}
      ref={ref}
      className={styles.draggedTab}
    >
      <div className={styles.title}>{title}</div>
    </div>
  );
});

const TabsBar = () => {
  const {
    state,
    actions: {
      createDocument,
      setActiveDocument,
      sortDocuments,
      reorderDocument,
      reorderTabGroup,
      setDocumentTabGroup,
      toggleTabGroupCollapsed,
    },
  } = useDocuments();
  const { t } = useTranslation();
  const {
    handleDeleteDocument,
    handleDeleteAllDocuments,
    handleUngroupTabGroup,
    handleDeleteTabGroupDocuments,
    handleSetDocumentTabGroup,
    handleCreateTabGroup,
    modalMarkup,
  } = useDocumentsActionConfirmation();
  useShortcutsHandler({
    handleDeleteDocument,
    handleDeleteAllDocuments,
    confirmationModalMarkup: modalMarkup,
  });
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [tabWidth, setTabWidth] = useState(0);
  const [hasScroll, setHasScroll] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLDivElement>>({});
  const { layout } = useLayoutToggles();
  const { state: scriptsState } = useScripts();
  const tabsMenuAnchorEl = useRef<HTMLDivElement | null>(null);
  const tabsMenuRef = useRef<HTMLDivElement | null>(null);
  const [tabsMenuOpen, setTabsMenuOpen] = useState<boolean>(false);
  const [createGroupAnchorEl, setCreateGroupAnchorEl] =
    useState<HTMLElement | null>(null);
  const { showOverlay } = useShortcutsOverlay();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const { activeDocumentId, tabGroups, documents } = state;

  const getSoleMemberGroupId = (documentId: string) => {
    const groupId =
      documents.find(document => document.id === documentId)?.groupId ?? null;

    return groupId &&
      documents.filter(document => document.groupId === groupId).length === 1
      ? groupId
      : null;
  };

  const draggedTabGroupId = draggedId
    ? (toTabGroupId(draggedId) ?? getSoleMemberGroupId(draggedId))
    : null;

  const barItems = useMemo(() => {
    const collapsedGroupIds = new Set(
      tabGroups
        .filter(tabGroup => tabGroup.collapsed)
        .map(tabGroup => tabGroup.id)
    );

    const draggedChipGroupId = draggedId ? toTabGroupId(draggedId) : null;

    return documents.flatMap((document, index) => {
      const tabGroup =
        tabGroups.find(group => group.id === document.groupId) ?? null;

      const items: Array<
        | { kind: "group"; tabGroup: TabGroup; collapsed: boolean }
        | { kind: "document"; document: WorkspaceDocument }
      > = [];

      const hideChip =
        !!tabGroup &&
        tabGroup.id === draggedTabGroupId &&
        tabGroup.id !== draggedChipGroupId;
      const hideMembers =
        !!tabGroup &&
        (collapsedGroupIds.has(tabGroup.id) ||
          tabGroup.id === draggedChipGroupId);

      if (
        tabGroup &&
        documents[index - 1]?.groupId !== tabGroup.id &&
        !hideChip
      ) {
        items.push({
          kind: "group",
          tabGroup,
          collapsed: collapsedGroupIds.has(tabGroup.id),
        });
      }

      if (!tabGroup || !hideMembers) {
        items.push({ kind: "document", document });
      }

      return items;
    });
  }, [documents, tabGroups, draggedId, draggedTabGroupId]);

  const barItemIds = useMemo(
    () =>
      barItems.map(item =>
        item.kind === "group"
          ? toGroupSortableId(item.tabGroup.id)
          : item.document.id
      ),
    [barItems]
  );

  const visibleDocuments = useMemo(
    () =>
      barItems.flatMap(item =>
        item.kind === "document" ? [item.document] : []
      ),
    [barItems]
  );

  const updateTabWidth = () => {
    const MAX_TAB_WIDTH = 140;
    const DEFAULT_TAB_WIDTH = MAX_TAB_WIDTH;
    const MIN_TAB_WIDTH = 70;
    const TAB_MARGIN = 2;
    const ADD_BUTTON_WIDTH = 40;

    if (draggedId) {
      return;
    }

    const tabsContainer = tabsContainerRef.current;

    if (!tabsContainer) {
      setTabWidth(DEFAULT_TAB_WIDTH);
      return;
    }

    const tabsContainerWidth = tabsContainer.clientWidth - ADD_BUTTON_WIDTH;

    const slots = visibleDocuments.length + tabGroups.length;

    const width = Math.min(
      MAX_TAB_WIDTH,
      Math.max(MIN_TAB_WIDTH, tabsContainerWidth / slots - TAB_MARGIN)
    );

    setHasScroll(tabsContainerWidth < slots * width);

    setTabWidth(width);
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateTabWidth();
    });

    resizeObserver.observe(tabsContainerRef.current!);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- it's ok here
  }, [barItems.length, tabsContainerRef.current]);

  useLayoutEffect(() => {
    updateTabWidth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- it's ok here
  }, [tabsContainerRef.current, barItems.length, layout.leftSidebar.expanded]);

  useEffect(() => {
    if (!activeDocumentId) {
      return;
    }

    const activeTabElement = tabRefs.current[activeDocumentId];

    if (activeTabElement) {
      setTimeout(() => {
        activeTabElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          // without this the vertical default ("start") scrolls the tab groups
          // bar above out of view whenever the active tab changes
          block: "nearest",
        });
      }, 0);
    }
  }, [activeDocumentId]);

  const getDocumentTitle = (documentId: string) => {
    const document = documents.find(item => item.id === documentId);

    return document
      ? getDocumentScript(document, scriptsState)?.title || ""
      : "";
  };

  const draggedTitle = useMemo(() => {
    if (!draggedId) {
      return "";
    }

    const tabGroup = tabGroups.find(group => group.id === draggedTabGroupId);

    return tabGroup ? tabGroup.name : getDocumentTitle(draggedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- it's ok here
  }, [draggedId, draggedTabGroupId, documents, tabGroups, scriptsState]);

  const activeDocument = documents.find(
    document => document.id === activeDocumentId
  );

  const handleCreateTabInGroup = async (tabGroupId: string) => {
    await createDocument(
      {
        context: activeDocument
          ? {
              database: { name: activeDocument.context.database.name },
              engine: { name: activeDocument.context.engine.name },
            }
          : undefined,
      },
      { groupId: tabGroupId }
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDraggedId(`${event.active.id}`);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedId(null);

    const { active, over } = event;

    if (!over || over.id === active.id) {
      return;
    }

    const activeId = `${active.id}`;
    const overId = `${over.id}`;

    const overTabGroupId = toTabGroupId(overId);
    const draggedGroup =
      toTabGroupId(activeId) ?? getSoleMemberGroupId(activeId);

    if (draggedGroup) {
      const overDocumentId = overTabGroupId
        ? documents.find(document => document.groupId === overTabGroupId)?.id
        : overId;

      if (overDocumentId) {
        reorderTabGroup(draggedGroup, overDocumentId);
      }

      return;
    }

    const overGroupId =
      documents.find(document => document.id === overId)?.groupId ?? null;

    if (
      overGroupId &&
      documents.find(document => document.groupId === overGroupId)?.id ===
        overId &&
      barItemIds.indexOf(activeId) > barItemIds.indexOf(overId)
    ) {
      setDocumentTabGroup(activeId, overGroupId, overGroupId);
      return;
    }

    if (overTabGroupId) {
      const fromLeft =
        barItemIds.indexOf(activeId) < barItemIds.indexOf(overId);

      const overTabGroupCollapsed = tabGroups.find(
        tabGroup => tabGroup.id === overTabGroupId
      )?.collapsed;

      if (overTabGroupCollapsed) {
        const members = documents.filter(
          document => document.groupId === overTabGroupId
        );
        const edgeDocument = fromLeft ? members.at(-1) : members[0];

        if (edgeDocument) {
          reorderDocument(activeId, edgeDocument.id);
        }

        return;
      }

      setDocumentTabGroup(
        activeId,
        fromLeft ? overTabGroupId : null,
        overTabGroupId
      );

      return;
    }

    reorderDocument(activeId, overId);
  };

  return (
    <div className={styles.container}>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <div className={styles.tabsWrapper}>
            <div
              className={styles.tabs}
              ref={tabsContainerRef}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setDraggedId(null)}
              >
                <SortableContext
                  strategy={horizontalListSortingStrategy}
                  items={barItemIds}
                >
                  {barItems.map((item, index) => {
                    const nextItem = barItems[index + 1];
                    const lastScrollableTab =
                      hasScroll && index === barItems.length - 1;

                    const isLastInGroup = (groupId: string | null) =>
                      !!groupId &&
                      !(
                        nextItem?.kind === "document" &&
                        nextItem.document.groupId === groupId
                      );

                    if (item.kind === "group") {
                      const members = documents.filter(
                        document => document.groupId === item.tabGroup.id
                      );

                      return (
                        <TabGroupWrapper
                          key={item.tabGroup.id}
                          tabGroup={item.tabGroup}
                          collapsed={item.collapsed}
                          memberTitles={members.map(member =>
                            getDocumentTitle(member.id)
                          )}
                          isDragged={draggedTabGroupId === item.tabGroup.id}
                          lastInGroup={isLastInGroup(item.tabGroup.id)}
                          lastScrollableTab={lastScrollableTab}
                          tabWidth={tabWidth}
                          onToggleCollapsed={() =>
                            toggleTabGroupCollapsed(item.tabGroup.id)
                          }
                          onUngroupAll={() =>
                            handleUngroupTabGroup(item.tabGroup.id)
                          }
                          onDeleteGroup={() =>
                            handleDeleteTabGroupDocuments(item.tabGroup.id)
                          }
                          onCreateTab={() =>
                            handleCreateTabInGroup(item.tabGroup.id)
                          }
                        />
                      );
                    }

                    const { document } = item;
                    const script = getDocumentScript(document, scriptsState);

                    return (
                      <TabWrapper
                        isDragged={draggedId === document.id}
                        key={document.id}
                        document={document}
                        tabGroup={
                          tabGroups.find(
                            group => group.id === document.groupId
                          ) ?? null
                        }
                        lastInGroup={isLastInGroup(document.groupId)}
                        withGap={
                          !nextItem ||
                          (!document.groupId &&
                            nextItem.kind === "document" &&
                            !nextItem.document.groupId)
                        }
                        handleDeleteDocument={handleDeleteDocument}
                        handleDeleteAllDocuments={handleDeleteAllDocuments}
                        handleSetDocumentTabGroup={handleSetDocumentTabGroup}
                        handleCreateTabInGroup={handleCreateTabInGroup}
                        handleCreateTabGroup={handleCreateTabGroup}
                        hasScroll={hasScroll}
                        lastScrollableTab={lastScrollableTab}
                        tabWidth={tabWidth}
                        tabRefs={tabRefs}
                        script={script}
                        // TODO enable this when we have backend sync
                        // isUnsaved={!!scriptState?.isUnsaved}
                        isUnsaved={false}
                      />
                    );
                  })}
                </SortableContext>

                <DragOverlay>
                  {draggedId ? (
                    <DraggedTab
                      id={draggedId}
                      title={draggedTitle}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>

              <div className={styles.addButtonWrapper}>
                <div
                  className={styles.addButton}
                  title={`${t(SHORTCUTS[ShortcutAction.ScriptOpenNew].title)}: ${getShortcutHint(
                    SHORTCUTS[ShortcutAction.ScriptOpenNew]
                  ).join(" + ")}`}
                  onClick={() => {
                    createDocument({
                      context: activeDocument
                        ? {
                            database: {
                              name: activeDocument.context.database.name,
                            },
                            engine: {
                              name: activeDocument.context.engine.name,
                            },
                          }
                        : undefined,
                    });
                  }}
                  data-testid="add-script-btn"
                >
                  <div className={styles.icon}>
                    <PlusIcon />
                  </div>
                </div>
              </div>
            </div>

            {hasScroll && <div className={styles.gradientBoundaryRight} />}
          </div>
        </div>

        <div className={styles.iconsWrapper}>
          <Tooltip
            title={t("workspace.tooltips.tab_options")}
            placement="bottom"
          >
            <div
              className={styles.icon}
              ref={tabsMenuAnchorEl}
              onClick={() => {
                setTabsMenuOpen(true);
              }}
              data-testid="other-tabs-menu-icon"
            >
              <TabsMenuIcon />
            </div>
          </Tooltip>
        </div>

        {tabsMenuOpen && (
          <Popover
            open={tabsMenuOpen}
            anchorEl={tabsMenuAnchorEl.current}
            classes={{ paper: styles.tabsMenuPaper }}
            onClose={() => {
              setTabsMenuOpen(false);
              // the anchor lives inside this popover so it must not outlive it
              setCreateGroupAnchorEl(null);
            }}
            onClick={e => {
              e.stopPropagation();
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <div ref={tabsMenuRef}>
              <ContextMenu>
                <ContextMenuItem
                  key="close-all"
                  text={t(SHORTCUTS[ShortcutAction.ScriptsCloseAll].title)}
                  testId="close-all-tabs"
                  secondaryText={getShortcutHint(
                    SHORTCUTS[ShortcutAction.ScriptsCloseAll]
                  ).join(" + ")}
                  onClick={e => {
                    e.stopPropagation();
                    setTabsMenuOpen(false);
                    handleDeleteAllDocuments([]);
                  }}
                />
                <ContextMenuItem
                  key="close-all-other"
                  text={t(SHORTCUTS[ShortcutAction.ScriptsCloseAllOther].title)}
                  testId="close-all-other-tabs"
                  secondaryText={getShortcutHint(
                    SHORTCUTS[ShortcutAction.ScriptsCloseAllOther]
                  ).join(" + ")}
                  onClick={e => {
                    e.stopPropagation();
                    setTabsMenuOpen(false);
                    handleDeleteAllDocuments(
                      activeDocumentId ? [activeDocumentId] : []
                    );
                  }}
                />
                <ContextMenuItem
                  key="create-tab-group"
                  text={t("workspace.tabs_menu.create_tab_group")}
                  testId="create-tab-group"
                  disabled={!activeDocument}
                  onClick={e => {
                    e.stopPropagation();
                    setCreateGroupAnchorEl(tabsMenuRef.current);
                  }}
                />
                <ContextMenuDivider />
                <ContextMenuItemsGroup
                  maxHeight={300}
                  isGroup={true}
                >
                  {documents.map(document => {
                    const script = getDocumentScript(document, scriptsState);

                    return (
                      <ContextMenuItem
                        checked={document.id === activeDocumentId}
                        key={document.id}
                        text={script?.title || ""}
                        testId={`script-tab-menu-item-${script?.title}`}
                        onClick={e => {
                          e.stopPropagation();
                          setTabsMenuOpen(false);
                          setActiveDocument(document.id);
                        }}
                      />
                    );
                  })}
                </ContextMenuItemsGroup>
                <ContextMenuDivider />
                <ContextMenuHeader
                  text={`${t("workspace.tabs_menu.sort_tabs_by")}:`}
                />
                <ContextMenuItem
                  key="newest-first"
                  text={t("workspace.tabs_menu.newest_first")}
                  testId="sort-tabs-newest-first"
                  onClick={e => {
                    e.stopPropagation();
                    setTabsMenuOpen(false);
                    sortDocuments(SortOrder.NewestFirst);
                  }}
                />

                <ContextMenuItem
                  key="newest-last"
                  text={t("workspace.tabs_menu.newest_last")}
                  testId="sort-tabs-newest-last"
                  onClick={e => {
                    e.stopPropagation();
                    setTabsMenuOpen(false);
                    sortDocuments(SortOrder.NewestLast);
                  }}
                />
                <ContextMenuDivider />
                <ContextMenuItem
                  key="keyboard-shortcuts"
                  text={t("workspace.tabs_menu.keyboard_shortcuts")}
                  secondaryText={getShortcutHint(
                    SHORTCUTS[ShortcutAction.ShortcutsOverlayOpen]
                  ).join(" + ")}
                  onClick={e => {
                    e.stopPropagation();
                    setTabsMenuOpen(false);
                    showOverlay();
                  }}
                />
              </ContextMenu>

              {createGroupAnchorEl && (
                <TabGroupPanel
                  anchorEl={createGroupAnchorEl}
                  attachTo="left"
                  onClose={() => setCreateGroupAnchorEl(null)}
                  onSubmit={tabGroup => {
                    handleCreateTabGroup(
                      tabGroup,
                      activeDocument ? [activeDocument.id] : []
                    );
                    setCreateGroupAnchorEl(null);
                    setTabsMenuOpen(false);
                  }}
                />
              )}
            </div>
          </Popover>
        )}

        {modalMarkup}
      </div>
    </div>
  );
};

export default TabsBar;
